import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import { FormProvider } from 'react-hook-form';
import { toast, Toaster } from 'sonner';

// Components
import Navbar from 'components/Navbar/Index';
import { Spinner } from 'components';
import CalculationPanel from './CalculationPanel';
import DealFormSteps from './DealFormSteps';
import CancelDealDialog from './CancelDealDialog';

// Hooks
import { useDealData } from '../hooks/useDealData';
import { useDealCalculation } from '../hooks/useDealCalculation';
import { useDealForm } from '../hooks/useDealForm';
import { useDealSubmission } from '../hooks/useDealSubmission';

// Utils
import {
    resetDealState,
    setPartnerDeal,
    setCreatedBy,
    setPartnerStatus,
    setPartnerMargin,
    setPartnerWalkinMargin,
    setPurposeId,
    setAmount,
    setCountry,
    setCurrency,
    setIsLoan,
    setMode,
    setPurposeIdCopy,
} from '../../../redux/features/CreateDeal';

const CreateNewDeal: React.FC = () => {
    const navigate = useNavigate();
    const { state }: { state: any } = useLocation();
    const dispatch = useDispatch();

    // State
    const [dealType, setDealType] = useState<string>("Self");
    const [servicePartner, setServicePartner] = useState<string | number>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddButton, setShowAddButton] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const [channelOfInteraction, setChannelOfInteraction] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    
    // Custom hooks
    const {
        purpose,
        countryOption,
        servicePartners,
        partnerOption,
        l2Partner,
        fetchServicePartners,
        updateCurrencyForCountry,
        allPurpose,
        refetch,
        branchOption: branchData,
    } = useDealData();
    const enhancedRefetch = {
        fetchPurpose: async () => {
            await refetch.fetchPurpose();
            setRefreshKey(prev => prev + 1);
        },
        fetchCountry: async () => {
            await refetch.fetchCountry();
            setRefreshKey(prev => prev + 1);
        },
        fetchPartner: async () => {
            await refetch.fetchPartner();
            setRefreshKey(prev => prev + 1);
        },
        fetchPartnerL2: async () => {
            await refetch.fetchPartnerL2();
            setRefreshKey(prev => prev + 1);
        },
    };

    const { isCalcLoading, rate, limitCustomerRate, limitCustomerRate2 } = useDealCalculation();

    const {
        form,
        activeField,
        setActiveField,
        currentStep,
        setCurrentStep,
        shouldDisplayField,
        getFormConfig,
        purposeSetter,
        handlePurposeChange,
        getBelongingBaseId    
    } = useDealForm(updateCurrencyForCountry, rate);

    const { handleSubmitDeal } = useDealSubmission();

    // Redux state
    const {
        partnerDeal,
        created_by,
        partner_status,
        partner_margin,
        partner_walkin_margin,
        purpose_id,
        currency,
        service_partner_id,
        isLoan,
        mode,
        country
    } = useAppSelector((state) => state.CreateDeal);

    // Event handlers
    const handleWalkinOrder = (): void => {
        const channelId = localStorage.getItem("channel_id");
        if (!channelId) return;

        dispatch(setPartnerDeal(false));
        setChannelOfInteraction("7");
        dispatch(setCreatedBy(channelId));

        dispatch(setPartnerStatus(""));
        dispatch(setPartnerMargin(""));
    };

    const handleDealTypeChange = (type: string) => {
        setDealType(type);

        if (type === "Self") {
            handleWalkinOrder();
            dispatch(setPartnerDeal(false));
            dispatch(setPartnerStatus(""));
        } else if (type === "Partner") {
            setChannelOfInteraction("12");
            if (!partnerDeal) {
                dispatch(setCreatedBy("-1"));
                dispatch(setPartnerStatus(""));
                dispatch(setPartnerMargin(""));
            }
            dispatch(setPartnerDeal(true));
        }
    };

    const handlePartnerChange = (partnerId: string) => {
        dispatch(setCreatedBy(partnerId));
        dispatch(setPartnerMargin(""));
        dispatch(
            setPartnerStatus(
                partnerOption?.find((item) => item.value.toString() === partnerId.toString())?.status || ""
            )
        );
    };

    const handleCancel = () => {
        localStorage.removeItem("deal_id");
        dispatch(resetDealState());
        navigate("/remit");
    };

    const handleSubmit = async (formData: any) => {
        setLoading(true);
        try {
            await handleSubmitDeal(formData, {
                channelOfInteraction,
                l2Partner,
                state,
            });

            // Navigate to success page
            const dealId = localStorage.getItem("deal_id");
            navigate("/remit/dealsuccess", {
                state: { deal_id: dealId },
                replace: true,
            });

            dispatch(resetDealState());
        } catch (error) {
            console.error('Deal submission failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (state?.opportunityId) { // Check if the relevant pre-fill data exists
            if (state.purpose_id) {
                const id = getBelongingBaseId(state.purpose_id);
                dispatch(setPurposeId(id));
                dispatch(setPurposeIdCopy(state.purpose_id));
                purposeSetter(id, state?.isLoan, state?.mode);
                form.setValue('purpose', id); // Important: Update react-hook-form as well
            }
            if (state?.isLoan) {
                dispatch(setIsLoan(state?.isLoan));
                form.setValue('isLoan', state?.isLoan);
            }            
            if (state.amount) {
                dispatch(setAmount(state.amount));
                form.setValue('amount', parseFloat(state.amount));
            }
            if (state.country) {
                dispatch(setCountry(state.country));
                form.setValue('country', state.country);
                const prefilledCurrency = updateCurrencyForCountry(state.country);
                dispatch(setCurrency(prefilledCurrency));
            }
        }
    }, [state]);

    useEffect(() => {
        if (state?.purpose_id) {
            const mode = allPurpose?.find((p) => p.id.toString() === state?.purpose_id?.toString())?.mode;
            dispatch(setMode(mode));
            form.setValue('mode', mode);
        }
    }, [state, allPurpose]);

    useEffect(() => {
        if (country) {
            const prefilledCurrency = updateCurrencyForCountry(country);
            dispatch(setCurrency(prefilledCurrency));
        }
    }, [country, updateCurrencyForCountry]);

    // Effects
    useEffect(() => {
        localStorage.setItem("deal_type", dealType);
    }, [dealType]);

    useEffect(() => {
        if (created_by === "") {
            handleWalkinOrder();
        }
    }, []);

    useEffect(() => {
        if (state?.partnerId) {
            setDealType("Partner");
            dispatch(setCreatedBy(state.partnerId.toString()));
            setChannelOfInteraction("12");
            dispatch(
                setPartnerStatus(
                    partnerOption?.find((item) => item.value === state.partnerId)?.status || ""
                )
            );
        }
    }, [state, partnerOption]);

    useEffect(() => {
        if (partner_status !== "pending") {
            dispatch(setPartnerMargin(""));
            dispatch(setPartnerWalkinMargin(""));
        }
    }, [partner_status]);

    // Service partners effect
    useEffect(() => {
        if (purpose_id && currency) {
            fetchServicePartners(purpose_id, currency).then((result) => {
                if (!result.success) {
                    toast.warning("Service Partners not available");
                    setActiveField(1);
                }
            });
        }
    }, [purpose_id, currency]);

    useEffect(() => {
        return () => {
            // This cleanup function runs when the component unmounts
            dispatch(resetDealState());
            localStorage.removeItem("deal_id"); // Also clear any relevant local storage items
            // You might also want to reset local component states if they are not derived from Redux
            setDealType("Self");
            setServicePartner("");
            setLoading(false);
            setShowAddButton(false);
            setOpen(false);
            setChannelOfInteraction("");
        };
    }, [dispatch]);


    return (
        <FormProvider {...form}>
            {loading && <Spinner />}
            <div className="p-4 sm:p-2 h-screen font-inter flex gap-2.5 flex-col">
                <Navbar className="lg:flex-shrink-0">
                    <div className="w-full flex justify-between">
                        <div className="flex gap-3 items-center">
                            <img src="images/sidebar_img/remit.svg" className="h-5" alt="" />
                            <div className="gap-2 flex flex-col">
                                <span className="text-sm font-semibold">Create Deal</span>
                                <span className="text-xs font-normal text-[#999999]">
                                    Book details to block forex rate for a limited time
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <CancelDealDialog onCancel={handleCancel} />
                        </div>
                    </div>
                </Navbar>

                <div className="w-full h-full md:flex-col gap-2.5 flex sm:flex-col lg:flex-grow lg:overflow-hidden">
                    <section className="w-[72%]  lg:flex-row md:w-full sm:w-full bg-white-A700 p-5 flex gap-4  sm:flex-col h-full overflow-auto rounded-xl">
                        <DealFormSteps
                            branches={branchData}
                            currentStep={currentStep}
                            onStepChange={setCurrentStep}
                            servicePartners={servicePartners}
                            servicePartner={servicePartner}
                            onServicePartnerChange={setServicePartner}
                            dealType={dealType}
                            onDealTypeChange={handleDealTypeChange}
                            partnerDeal={partnerDeal}
                            partnerOption={partnerOption}
                            createdBy={created_by}
                            onPartnerChange={handlePartnerChange}
                            showAddButton={showAddButton}
                            onAddPartner={() => setOpen(true)}
                            onInputChange={(val) => {
                                const hasMatch = partnerOption?.some((option) =>
                                    option.label.toLowerCase().includes(val.toLowerCase())
                                );
                                setShowAddButton(val.trim().length > 0 && !hasMatch);
                            }}
                            purpose={purpose}
                            countryOption={countryOption}
                            isCalcLoading={isCalcLoading}
                            limitCustomerRate={limitCustomerRate}
                            limitCustomerRate2={limitCustomerRate2}
                            onSubmit={handleSubmit}
                            disabled={state?.partnerId}
                            currency={currency}
                            activeField={activeField}
                            setActiveField={setActiveField}
                            shouldDisplayField={shouldDisplayField}
                            getFormConfig={getFormConfig}
                            refetch={enhancedRefetch}
                        />
                       
                    </section>
                    <CalculationPanel isCalcLoading={isCalcLoading} />
                 
                </div>

                <Toaster theme="dark" position="top-right" />
            </div>
        </FormProvider>
    );
};

export default CreateNewDeal;